import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNow } from "date-fns";
import { Loader2, Mail, Search, StickyNote, AlertTriangle, Archive, ArchiveRestore, Paperclip, Trash2, Download, Grape, LayoutGrid, List, PhoneOutgoing, CalendarClock, Plus, FileText } from "lucide-react";
import { toast } from "sonner";
import { Modal } from "@/components/Modal";
import { api, API, fmtTime, fmtDate, formatApiError } from "@/lib/api";
import { EVENT_TYPES, OCCASIONS, HOLIDAYS } from "@/lib/eventOptions";

const STAGES = [
  { id: "new", label: "Inquiry Received", chip: "bg-green-100 text-green-800", dot: "bg-green-600" },
  { id: "contacted", label: "Contacted", chip: "bg-amber-100 text-amber-900", dot: "bg-amber-500" },
  { id: "deposit_paid", label: "Deposit Paid", chip: "bg-gold/20 text-amber-900", dot: "bg-gold" },
  { id: "paid_in_full", label: "Paid in Full", chip: "bg-ink text-bone", dot: "bg-ink" },
  { id: "event_complete", label: "Event Complete", chip: "bg-stone-800 text-bone", dot: "bg-stone-800" },
];
const ALL_STATUSES = [...STAGES, { id: "lost", label: "Deal Lost", chip: "bg-red-100 text-red-800", dot: "bg-red-600" }];
const ACTIVE_STAGES = ["new", "contacted", "deposit_paid", "paid_in_full"];
const WEEK_MS = 7 * 86400000;

const KIND_FILTERS = [
  { id: "", label: "All types" },
  { id: "venue", label: "Venue" },
  { id: "charcuterie", label: "Charcuterie" },
];

const statusOf = (id) => ALL_STATUSES.find((s) => s.id === id);

function needsFollowUp(i) {
  if (i.archived || !ACTIVE_STAGES.includes(i.status)) return false;
  const lastTouch = i.last_contacted || (i.status === "new" ? i.created_at : i.updated_at);
  return Date.now() - new Date(lastTouch).getTime() > WEEK_MS;
}

export default function InquiriesTab() {
  const qc = useQueryClient();
  const [view, setView] = useState("pipeline");
  const [kind, setKind] = useState("");
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [lostTarget, setLostTarget] = useState(null);
  const [lostReason, setLostReason] = useState("");
  const [savingLost, setSavingLost] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", phone: "", email: "", kind: "venue", event_type: "", event_type_other: "", holiday: "", guest_count: "", date: "", details: "" });
  const [savingAdd, setSavingAdd] = useState(false);
  const [addonAmount, setAddonAmount] = useState("");
  const [savingAddon, setSavingAddon] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInput = useRef(null);

  const listQ = useQuery({
    queryKey: ["admin-inquiries", q, kind, archivedOnly],
    queryFn: () => api.get("/admin/inquiries", {
      params: { q: q || undefined, kind: kind || undefined, archived: archivedOnly || undefined },
    }).then((r) => r.data),
  });

  const items = listQ.data?.items || [];
  const counts = listQ.data?.counts || {};
  const selected = items.find((i) => i.inquiry_id === selectedId) || null;
  const todayIso = format(new Date(), "yyyy-MM-dd");
  const isPastEvent = selected?.date && selected.date < todayIso;

  const patch = async (id, payload, silent = false) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, payload);
      await qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      return true;
    } catch (err) {
      if (!silent) toast.error(formatApiError(err));
      return false;
    }
  };

  const moveCard = (inquiry, newStatus) => {
    if (!newStatus || newStatus === inquiry.status) return;
    if (newStatus === "lost") {
      setLostTarget(inquiry);
      setLostReason(inquiry.lost_reason || "");
      return;
    }
    patch(inquiry.inquiry_id, { status: newStatus });
    toast.success(`Moved to ${statusOf(newStatus)?.label}.`);
  };

  const markContactedToday = (inquiry, e) => {
    e?.stopPropagation();
    const payload = { last_contacted: todayIso };
    if (inquiry.status === "new") payload.status = "contacted";
    patch(inquiry.inquiry_id, payload);
    toast.success("Contact logged for today.");
  };

  useEffect(() => { setAddonAmount(selected?.addon_amount ?? ""); }, [selected]);

  const saveAddon = async () => {
    if (!selected) return;
    setSavingAddon(true);
    const ok = await patch(selected.inquiry_id, { addon_amount: addonAmount === "" ? 0 : Number(addonAmount) });
    setSavingAddon(false);
    if (ok) toast.success("Charcuterie add-on saved — it will appear on the agreement.");
  };

  const deleteCard = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/inquiries/${selected.inquiry_id}`);
      await qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      setConfirmingDelete(false);
      setSelectedId(null);
      toast.success("Card deleted.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const createCard = async (e) => {
    e.preventDefault();
    setSavingAdd(true);
    try {
      await api.post("/admin/inquiries", {
        name: addForm.name.trim(),
        phone: addForm.phone.trim() || null,
        email: addForm.email.trim() || null,
        kind: addForm.kind,
        event_type: addForm.event_type || null,
        event_type_other: addForm.kind === "venue" && addForm.event_type === "Other" ? addForm.event_type_other.trim() || null : null,
        holiday: addForm.kind === "charcuterie" && addForm.event_type === "Holiday party" ? addForm.holiday || null : null,
        guest_count: addForm.guest_count ? Number(addForm.guest_count) : null,
        date: addForm.date || null,
        details: addForm.details.trim() || null,
      });
      await qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      setAdding(false);
      setAddForm({ name: "", phone: "", email: "", kind: "venue", event_type: "", event_type_other: "", holiday: "", guest_count: "", date: "", details: "" });
      toast.success("Card added to Inquiry Received.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingAdd(false);
    }
  };

  const confirmLost = async (e) => {    e.preventDefault();
    if (!lostReason.trim()) { toast.error("Please add a reason."); return; }
    setSavingLost(true);
    const ok = await patch(lostTarget.inquiry_id, { status: "lost", lost_reason: lostReason.trim() });
    setSavingLost(false);
    if (ok) { setLostTarget(null); toast.success("Moved to Deal Lost."); }
  };

  const addNote = async (e) => {
    e.preventDefault();
    if (!note.trim() || !selected) return;
    setSavingNote(true);
    await patch(selected.inquiry_id, { note: note.trim() });
    setNote("");
    setSavingNote(false);
    toast.success("Note added.");
  };

  const toggleArchive = async () => {
    const ok = await patch(selected.inquiry_id, { archived: !selected.archived });
    if (ok) toast.success(selected.archived ? "Removed from archive." : "Archived — we'll remind you before next year's date.");
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/inquiries/${selected.inquiry_id}/attachments`, fd);
      await qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      toast.success("Attachment added.");
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const deleteFile = async (a) => {
    if (!window.confirm(`Remove "${a.filename}"?`)) return;
    try {
      await api.delete(`/admin/attachments/${a.attachment_id}`);
      await qc.invalidateQueries({ queryKey: ["admin-inquiries"] });
      toast.success("Attachment removed.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const CardBody = ({ i }) => {
    const followUp = needsFollowUp(i);
    return (
      <div
        draggable
        onDragStart={(e) => e.dataTransfer.setData("text/plain", i.inquiry_id)}
        onClick={() => setSelectedId(i.inquiry_id)}
        data-testid={`admin-inquiry-row-${i.reference}`}
        className={`card cursor-pointer p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md
          ${selectedId === i.inquiry_id ? "ring-2 ring-ink" : ""}
          ${followUp ? "ring-2 ring-amber-500/70" : ""}`}>
        <div className="flex items-start justify-between gap-2">
          <p className="flex flex-wrap items-center gap-2 font-serif text-base leading-snug text-ink">
            {i.name}
            {i.kind === "charcuterie" ? (
              <span className="rounded-full bg-gold/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-900">
                <Grape className="mr-0.5 inline h-3 w-3" />Charcuterie
              </span>
            ) : (
              <span className="rounded-full bg-stone-200/80 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-600">
                Venue
              </span>
            )}
          </p>
          {i.archived && (
            <span className="flex shrink-0 items-center gap-1 rounded-full bg-stone-200 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-stone-600">
              <Archive className="h-3 w-3" />Archived
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-stone-500">
          {i.event_type}{i.holiday ? ` · ${i.holiday}` : ""}{i.event_type_other ? ` (${i.event_type_other})` : ""} · {i.guest_count} guests
        </p>
        <p className="mt-1 text-[11px] text-stone-500">
          {i.date ? `${fmtDate(i.date)}${i.start_time ? ` · ${fmtTime(i.start_time)}` : ""}` : "Date not decided"}
        </p>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-black/5 pt-2.5">
          <button onClick={(e) => { e.stopPropagation(); markContactedToday(i, e); }}
            data-testid={`admin-contact-today-${i.reference}`}
            title="Log that you made contact today"
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold transition-colors
              ${followUp ? "bg-amber-100 text-amber-900 hover:bg-amber-200" : "bg-bone2 text-stone-500 hover:bg-stone-200"}`}>
            {followUp ? <CalendarClock className="h-3 w-3" /> : <PhoneOutgoing className="h-3 w-3" />}
            {followUp ? "Follow up!" : i.last_contacted ? `Contacted ${fmtDate(i.last_contacted)}` : "Log contact"}
          </button>
          <div className="flex items-center gap-2 text-stone-400">
            {(i.attachments || []).length > 0 && (
              <span className="flex items-center gap-1 text-[10px]"><Paperclip className="h-3 w-3" />{i.attachments.length}</span>
            )}
            <select value={i.status} onClick={(e) => e.stopPropagation()}
              onChange={(e) => { e.stopPropagation(); moveCard(i, e.target.value); }}
              data-testid={`admin-card-stage-${i.reference}`} aria-label={`Move ${i.name} to another stage`}
              className="cursor-pointer rounded-full border border-black/10 bg-white px-2 py-1 text-[10px] font-semibold text-stone-600">
              {ALL_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div data-testid="admin-inquiries-tab">
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="flex rounded-full border border-black/10 bg-white p-1" role="tablist" aria-label="View">
          <button onClick={() => setView("pipeline")} role="tab" aria-selected={view === "pipeline"} data-testid="admin-view-pipeline"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${view === "pipeline" ? "bg-ink text-bone" : "text-stone-500 hover:text-ink"}`}>
            <LayoutGrid className="h-3.5 w-3.5" /> Pipeline
          </button>
          <button onClick={() => setView("list")} role="tab" aria-selected={view === "list"} data-testid="admin-view-list"
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${view === "list" ? "bg-ink text-bone" : "text-stone-500 hover:text-ink"}`}>
            <List className="h-3.5 w-3.5" /> List
          </button>
        </div>
        <div className="flex gap-2" role="tablist" aria-label="Inquiry type">
          {KIND_FILTERS.map((k) => (
            <button key={k.id} onClick={() => setKind(k.id)} role="tab" aria-selected={kind === k.id}
              data-testid={`admin-kind-${k.id || "all"}`}
              className={`rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${kind === k.id ? "border-ink bg-white text-ink" : "border-black/10 text-stone-400 hover:text-stone-600"}`}>
              {k.label}{k.id === "charcuterie" && counts.charcuterie ? ` (${counts.charcuterie})` : ""}
            </button>
          ))}
          <button onClick={() => setArchivedOnly(!archivedOnly)} data-testid="admin-filter-archived"
            className={`rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${archivedOnly ? "border-ink bg-white text-ink" : "border-black/10 text-stone-400 hover:text-stone-600"}`}>
            <Archive className="mr-1 inline h-3 w-3" />Archived ({counts.archived || 0})
          </button>
        </div>
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, reference…"
            data-testid="admin-inquiry-search" className="field !pl-10" aria-label="Search inquiries" />
        </div>
        <button onClick={() => setAdding(true)} data-testid="admin-add-card-btn" className="btn-dark !px-5 !py-2.5 text-xs">
          <Plus className="h-4 w-4" /> Add card
        </button>
      </div>

      {listQ.isLoading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>}

      {view === "pipeline" && !listQ.isLoading && (
        <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4" data-testid="admin-pipeline-board">
          {STAGES.map((stage) => {
            const stageItems = items.filter((i) => i.status === stage.id && (archivedOnly ? true : !i.archived));
            return (
              <div key={stage.id}
                onDragOver={(e) => { e.preventDefault(); setDragOver(stage.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(null);
                  const id = e.dataTransfer.getData("text/plain");
                  const inq = items.find((x) => x.inquiry_id === id);
                  if (inq) moveCard(inq, stage.id);
                }}
                data-testid={`admin-stage-${stage.id}`}
                className={`w-72 shrink-0 snap-start rounded-2xl bg-bone2/70 p-3 transition-colors ${dragOver === stage.id ? "ring-2 ring-ink/40" : ""}`}>
                <div className="mb-3 flex items-center justify-between px-1.5 pt-1">
                  <span className={`flex items-center gap-2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${stage.chip}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${stage.id === "paid_in_full" || stage.id === "event_complete" ? "bg-bone" : stage.dot}`} />
                    {stage.label}
                  </span>
                  <span className="text-xs font-semibold text-stone-400">{stageItems.length}</span>
                </div>
                <div className="space-y-3">
                  {stageItems.map((i) => <CardBody key={i.inquiry_id} i={i} />)}
                  {stageItems.length === 0 && (
                    <p className="rounded-xl border border-dashed border-stone-300/70 p-4 text-center text-[11px] text-stone-400">
                      Drop cards here
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver("lost"); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              const id = e.dataTransfer.getData("text/plain");
              const inq = items.find((x) => x.inquiry_id === id);
              if (inq) moveCard(inq, "lost");
            }}
            data-testid="admin-stage-lost"
            className={`w-72 shrink-0 snap-start rounded-2xl bg-red-50/60 p-3 transition-colors ${dragOver === "lost" ? "ring-2 ring-red-700/40" : ""}`}>
            <div className="mb-3 flex items-center justify-between px-1.5 pt-1">
              <span className="flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-red-800">
                <span className="h-1.5 w-1.5 rounded-full bg-red-600" /> Deal Lost
              </span>
              <span className="text-xs font-semibold text-stone-400">{items.filter((i) => i.status === "lost").length}</span>
            </div>
            <div className="space-y-3">
              {items.filter((i) => i.status === "lost" && (archivedOnly ? true : !i.archived)).map((i) => <CardBody key={i.inquiry_id} i={i} />)}
              {items.filter((i) => i.status === "lost" && (archivedOnly ? true : !i.archived)).length === 0 && (
                <p className="rounded-xl border border-dashed border-red-300/60 p-4 text-center text-[11px] text-red-400">
                  Drag here to archive a deal as lost
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {view === "list" && !listQ.isLoading && (
        <div className="grid items-start gap-6 lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-6" data-testid="admin-inquiry-list">
            {items.length === 0 && (
              <div className="card p-12 text-center">
                <p className="font-serif text-2xl text-ink">No inquiries here</p>
                <p className="mt-2 text-sm text-stone-500">Try a different filter — new inquiries land in “All”.</p>
              </div>
            )}
            {items.map((i) => <CardBody key={i.inquiry_id} i={i} />)}
          </div>
          <div className="hidden lg:col-span-6 lg:block">
            <div className="card p-12 text-center">
              <p className="font-serif text-2xl text-ink">Select an inquiry</p>
              <p className="mt-2 text-sm text-stone-500">Full details, attachments, outreach notes, and status live here.</p>
            </div>
          </div>
        </div>
      )}

      <Modal open={!!selected} onClose={() => setSelectedId(null)} title={selected ? `${selected.name} · ${selected.reference}` : ""} testid="admin-inquiry-modal">
        {selected && (
          <div data-testid="admin-inquiry-detail">
            <div className="flex flex-wrap items-center gap-3">
              <select value={selected.status} data-testid="admin-inquiry-status-select"
                onChange={(e) => moveCard(selected, e.target.value)}
                className="field !w-auto text-xs font-semibold" aria-label="Inquiry status">
                {ALL_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
              <div className="flex items-center gap-2">
                <label htmlFor="last-contact" className="text-[10px] font-bold uppercase tracking-wider text-stone-400">Last contact</label>
                <input id="last-contact" type="date" value={selected.last_contacted || ""} max={todayIso}
                  data-testid="admin-last-contact-input"
                  onChange={(e) => patch(selected.inquiry_id, { last_contacted: e.target.value })}
                  className="field !w-auto !px-2.5 !py-1.5 text-xs" />
              </div>
            </div>

            {isPastEvent && !selected.archived && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-600/20" data-testid="admin-archive-prompt">
                <p className="text-xs leading-relaxed text-amber-900">
                  <span className="font-bold uppercase tracking-wider">Event date has passed.</span><br />
                  {selected.recurring
                    ? "Archive it to keep the details for reference and get a follow-up reminder before next year's date."
                    : "Archive it to keep the details for reference. One-time events don't get annual follow-up reminders."}
                </p>
                <button onClick={toggleArchive} data-testid="admin-archive-btn" className="btn-dark !px-4 !py-2 text-xs">
                  <Archive className="h-3.5 w-3.5" /> Archive event
                </button>
              </div>
            )}
            {selected.archived && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-stone-100 p-4 ring-1 ring-stone-300/40" data-testid="admin-archived-banner">
                <p className="text-xs leading-relaxed text-stone-600">
                  <span className="font-bold uppercase tracking-wider">Archived.</span><br />
                  Saved for reference — a follow-up reminder appears 45 days before the anniversary.
                </p>
                <button onClick={toggleArchive} data-testid="admin-unarchive-btn" className="btn-ghost !px-4 !py-2 text-xs">
                  <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
                </button>
              </div>
            )}
            {selected.status === "lost" && selected.lost_reason && (
              <div className="mt-5 rounded-xl bg-red-50 p-4 text-xs leading-relaxed text-red-800 ring-1 ring-red-700/15" data-testid="admin-lost-reason">
                <span className="font-bold uppercase tracking-wider">Lost — </span>{selected.lost_reason}
              </div>
            )}

            <dl className="mt-6 grid grid-cols-1 gap-x-6 gap-y-4 border-t border-black/5 pt-6 text-sm sm:grid-cols-2">
              <div><dt className="label !mb-1">Email</dt><dd><a className="text-ink underline underline-offset-2" href={`mailto:${selected.email}`}>{selected.email}</a></dd></div>
              <div><dt className="label !mb-1">Phone</dt><dd className="text-stone-700">{selected.phone}</dd></div>
              <div><dt className="label !mb-1">{selected.kind === "charcuterie" ? "Service" : "Event"}</dt><dd className="text-stone-700">{selected.event_type}{selected.event_type_other ? ` — ${selected.event_type_other}` : ""}</dd></div>
              <div><dt className="label !mb-1">Guests</dt><dd className="text-stone-700">{selected.guest_count}{selected.over_capacity ? " (over capacity)" : ""}</dd></div>
              <div><dt className="label !mb-1">Date{selected.start_time ? " & time" : ""}</dt><dd className="text-stone-700">{selected.date ? `${fmtDate(selected.date)}${selected.start_time ? ` · ${fmtTime(selected.start_time)} – ${fmtTime(selected.end_time)}` : ""}` : "Not decided"}</dd></div>
              <div><dt className="label !mb-1">Flexibility</dt><dd className="text-stone-700">{selected.date_flexible || "—"}</dd></div>
              {selected.location && <div className="sm:col-span-2"><dt className="label !mb-1">Setup location</dt><dd className="text-stone-700">{selected.location}</dd></div>}
              {selected.charcuterie_style && <div><dt className="label !mb-1">Charcuterie style</dt><dd className="text-stone-700">{selected.charcuterie_style}</dd></div>}
              {selected.holiday && <div><dt className="label !mb-1">Holiday</dt><dd className="text-stone-700">{selected.holiday}</dd></div>}
              {selected.services?.length > 0 && selected.kind !== "charcuterie" && (
                <div className="sm:col-span-2"><dt className="label !mb-1">Charcuterie interest</dt>
                  <dd className="text-stone-700">{selected.services.join(", ")}</dd></div>)}
              {selected.outside_vendors && <div><dt className="label !mb-1">Outside vendors</dt><dd className="text-stone-700">{selected.outside_vendors}</dd></div>}
              {selected.details && <div className="sm:col-span-2"><dt className="label !mb-1">Details</dt>
                <dd className="whitespace-pre-wrap rounded-xl bg-bone2 p-4 text-xs leading-relaxed text-stone-700">{selected.details}</dd></div>}
            </dl>

            {selected.kind === "venue" && (
              <div className="mt-6 rounded-xl bg-bone2 p-4 ring-1 ring-black/5" data-testid="admin-addon-section">
                <label htmlFor="addon-amount" className="label !mb-1.5">Charcuterie add-on ($)</label>
                <div className="flex gap-2">
                  <input id="addon-amount" type="number" min="0" step="0.01" value={addonAmount}
                    onChange={(e) => setAddonAmount(e.target.value)} data-testid="admin-addon-amount"
                    className="field" placeholder="e.g. 450" />
                  <button onClick={saveAddon} disabled={savingAddon} data-testid="admin-addon-save"
                    className="btn-dark shrink-0 !px-5 !py-2.5 text-xs disabled:opacity-50">
                    {savingAddon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                  </button>
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-stone-500">
                  Their quoted boards or grazing table price. It's added to the rental fee as the total event cost on the generated agreement.
                </p>
              </div>
            )}

            <div className="mt-6 grid gap-3">
              <a href={`mailto:${selected.email}?subject=Re: Your inquiry ${selected.reference} — The Owl's Nest`}
                data-testid="admin-inquiry-reply" className="btn-dark w-full !py-3 text-xs">
                <Mail className="h-4 w-4" /> Reply by email
              </a>
              {selected.kind === "venue" && (
                <a href={`${API}/admin/inquiries/${selected.inquiry_id}/agreement`} target="_blank" rel="noopener noreferrer"
                  data-testid="admin-agreement-btn" className="btn-ghost w-full !py-3 text-xs">
                  <FileText className="h-4 w-4" /> Generate rental agreement (PDF)
                </a>
              )}
            </div>

            <div className="mt-8 border-t border-black/5 pt-6">
              <p className="label"><Paperclip className="mr-1.5 inline h-3.5 w-3.5" />Attachments (private)</p>
              <div className="space-y-2" data-testid="admin-attachments-list">
                {(selected.attachments || []).map((a) => (
                  <div key={a.attachment_id} className="flex items-center justify-between gap-3 rounded-xl bg-bone2 px-4 py-2.5" data-testid={`admin-attachment-${a.attachment_id}`}>
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium text-stone-700">{a.filename}</p>
                      <p className="text-[10px] text-stone-400">{(a.size / 1024).toFixed(0)} KB · {a.uploaded_by} · {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}</p>
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <a href={`${API}/admin/attachments/${a.attachment_id}/download`} target="_blank" rel="noopener noreferrer"
                        data-testid={`admin-attachment-download-${a.attachment_id}`} aria-label={`Download ${a.filename}`}
                        className="rounded-full p-2 text-stone-400 transition-colors hover:bg-white hover:text-ink"><Download className="h-4 w-4" /></a>
                      <button onClick={() => deleteFile(a)} aria-label={`Remove ${a.filename}`} data-testid={`admin-attachment-delete-${a.attachment_id}`}
                        className="rounded-full p-2 text-stone-400 transition-colors hover:bg-red-50 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                {(selected.attachments || []).length === 0 && (
                  <p className="text-xs text-stone-400">Nothing attached yet — signed contracts, floor plans, inspiration photos.</p>
                )}
              </div>
              <input ref={fileInput} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.doc,.docx,.txt"
                onChange={uploadFile} data-testid="admin-attachment-input" />
              <button onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="admin-attachment-upload-btn"
                className="btn-ghost mt-3 w-full !py-2.5 text-xs disabled:opacity-50">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Paperclip className="h-3.5 w-3.5" /> Attach a file</>}
              </button>
            </div>

            <div className="mt-8 border-t border-black/5 pt-6">
              <p className="label"><StickyNote className="mr-1.5 inline h-3.5 w-3.5" />Internal notes (private)</p>
              <div className="space-y-3">
                {(selected.notes || []).map((n, idx) => (
                  <div key={idx} className="rounded-xl bg-bone2 p-3.5 text-xs leading-relaxed text-stone-700">
                    {n.text}
                    <p className="mt-1.5 text-[10px] text-stone-400">{n.by} · {formatDistanceToNow(new Date(n.at), { addSuffix: true })}</p>
                  </div>
                ))}
                {(selected.notes || []).length === 0 && <p className="text-xs text-stone-400">No notes yet — e.g. “Called, left voicemail”, “Emailed quote”.</p>}
              </div>
              <form onSubmit={addNote} className="mt-4 flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input"
                  className="field" placeholder="Add a note about your outreach…" aria-label="Add internal note" />
                <button type="submit" disabled={savingNote || !note.trim()} data-testid="admin-note-add"
                  className="btn-dark !px-5 !py-2.5 text-xs disabled:opacity-50">Add</button>
              </form>
            </div>

            {selected.status_history?.length > 0 && (
              <div className="mt-6 border-t border-black/5 pt-5">
                <p className="label">History</p>
                <ul className="space-y-1.5 text-[11px] text-stone-500">
                  {selected.status_history.map((h, idx) => (
                    <li key={idx}>{h.from ? `${statusOf(h.from)?.label || h.from} → ` : ""}{statusOf(h.to)?.label || h.to} · {h.by} · {formatDistanceToNow(new Date(h.at), { addSuffix: true })}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-8 border-t border-black/5 pt-5">
              <button onClick={() => setConfirmingDelete(true)} data-testid="admin-delete-card-btn"
                className="text-xs font-semibold text-red-700 underline-offset-2 transition-colors hover:text-red-800 hover:underline">
                <Trash2 className="mr-1 inline h-3.5 w-3.5" />Delete this card
              </button>
              <p className="mt-1.5 text-[11px] leading-relaxed text-stone-400">
                Permanently removes the inquiry with its notes, attachments, and reminders. Handy for test or duplicate cards.
              </p>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={adding} onClose={() => setAdding(false)} title="Add a card" testid="admin-add-card-modal">
        <form onSubmit={createCard} className="space-y-4">
          <p className="text-xs leading-relaxed text-stone-500">
            For inquiries that come in by phone, text, or in person. It lands in <span className="font-semibold">Inquiry Received</span> and follows the same pipeline.
          </p>
          <div className="flex gap-2" role="radiogroup" aria-label="Inquiry type">
            {[["venue", "Venue"], ["charcuterie", "Charcuterie"]].map(([id, label]) => (
              <button type="button" key={id} onClick={() => setAddForm({ ...addForm, kind: id, event_type: "", event_type_other: "", holiday: "" })}
                data-testid={`admin-add-kind-${id}`} aria-pressed={addForm.kind === id}
                className={`flex-1 rounded-xl border px-4 py-2.5 text-xs font-semibold transition-colors ${addForm.kind === id ? "border-ink bg-ink text-bone" : "border-black/10 text-stone-500 hover:border-black/30"}`}>
                {label}
              </button>
            ))}
          </div>
          <div>
            <label htmlFor="add-name" className="label">Name *</label>
            <input id="add-name" required value={addForm.name} data-testid="admin-add-name"
              onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className="field" placeholder="Customer name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-phone" className="label">Phone</label>
              <input id="add-phone" type="tel" value={addForm.phone} data-testid="admin-add-phone"
                onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} className="field" placeholder="(817) …" />
            </div>
            <div>
              <label htmlFor="add-email" className="label">Email</label>
              <input id="add-email" type="email" value={addForm.email} data-testid="admin-add-email"
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} className="field" placeholder="Optional" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="add-type" className="label">{addForm.kind === "charcuterie" ? "Occasion" : "Event type"}</label>
              <select id="add-type" value={addForm.event_type} data-testid="admin-add-type"
                onChange={(e) => setAddForm({ ...addForm, event_type: e.target.value, event_type_other: "", holiday: "" })} className="field">
                <option value="">Choose one…</option>
                {(addForm.kind === "charcuterie" ? OCCASIONS : EVENT_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="add-guests" className="label">Guests</label>
              <input id="add-guests" type="number" min="0" value={addForm.guest_count} data-testid="admin-add-guests"
                onChange={(e) => setAddForm({ ...addForm, guest_count: e.target.value })} className="field" placeholder="e.g. 50" />
            </div>
          </div>
          {addForm.kind === "venue" && addForm.event_type === "Other" && (
            <div>
              <label htmlFor="add-type-other" className="label">Tell us what it is</label>
              <input id="add-type-other" value={addForm.event_type_other} data-testid="admin-add-type-other"
                onChange={(e) => setAddForm({ ...addForm, event_type_other: e.target.value })} className="field" placeholder="e.g. Book launch" />
            </div>
          )}
          {addForm.kind === "charcuterie" && addForm.event_type === "Holiday party" && (
            <div>
              <label htmlFor="add-holiday" className="label">Which holiday?</label>
              <select id="add-holiday" value={addForm.holiday} data-testid="admin-add-holiday"
                onChange={(e) => setAddForm({ ...addForm, holiday: e.target.value })} className="field">
                <option value="">Choose one…</option>
                {HOLIDAYS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="add-date" className="label">Date (if known)</label>
            <input id="add-date" type="date" value={addForm.date} data-testid="admin-add-date"
              onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} className="field" />
          </div>
          <div>
            <label htmlFor="add-details" className="label">Notes</label>
            <textarea id="add-details" rows={2} value={addForm.details} data-testid="admin-add-details"
              onChange={(e) => setAddForm({ ...addForm, details: e.target.value })} className="field"
              placeholder="What they asked for, where, budget…" />
          </div>
          <button type="submit" disabled={savingAdd} data-testid="admin-add-save" className="btn-dark w-full disabled:opacity-60">
            {savingAdd ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add to pipeline"}
          </button>
        </form>
      </Modal>

      <Modal open={!!lostTarget} onClose={() => setLostTarget(null)} title="Move to Deal Lost" testid="admin-lost-modal">
        <form onSubmit={confirmLost} className="space-y-5">
          <p className="text-sm leading-relaxed text-stone-600">
            Why did <span className="font-semibold text-ink">{lostTarget?.name}</span> decide not to book?
            This stays private and helps you spot patterns later.
          </p>
          <div>
            <label htmlFor="lost-reason" className="label">Reason *</label>
            <textarea id="lost-reason" required rows={3} value={lostReason} data-testid="admin-lost-reason-input"
              onChange={(e) => setLostReason(e.target.value)} className="field"
              placeholder="e.g. Chose another venue, budget, date didn't work…" />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={savingLost} data-testid="admin-lost-confirm"
              className="btn-dark flex-1 !bg-red-800 hover:!bg-red-900 disabled:opacity-60">
              {savingLost ? <Loader2 className="h-4 w-4 animate-spin" /> : "Move to Deal Lost"}
            </button>
            <button type="button" onClick={() => setLostTarget(null)} className="btn-ghost !px-5 !py-2.5 text-xs">Cancel</button>
          </div>
        </form>
      </Modal>

      <Modal open={confirmingDelete} onClose={() => setConfirmingDelete(false)} title="Delete this card?" testid="admin-delete-modal">
        <div className="space-y-5">
          <p className="text-sm leading-relaxed text-stone-600" data-testid="admin-delete-warning">
            <span className="font-semibold text-ink">{selected?.name}</span> ({selected?.reference}) will be permanently
            deleted, along with its notes, attachments, and any follow-up reminders. This can&rsquo;t be undone.
          </p>
          <div className="flex gap-3">
            <button onClick={deleteCard} disabled={deleting} data-testid="admin-delete-confirm-btn"
              className="btn-dark flex-1 !bg-red-800 hover:!bg-red-900 disabled:opacity-60">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
            </button>
            <button type="button" onClick={() => setConfirmingDelete(false)} data-testid="admin-delete-cancel"
              className="btn-ghost !px-5 !py-2.5 text-xs">Keep card</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
